import type { UserMembership, UserProfile } from '@/src/api/authApi';
import type { Membership } from '@/src/api/membershipsApi';

/**
 * Two questions, one classifier. [[deriveMemberState]] puts the user in exactly
 * one state; [[deriveMembershipUpsell]] (what to pitch) and
 * [[derivePurchaseGate]] (may they buy THIS plan) are each a switch on it.
 */

const priceOf = (plan: Membership) => plan.priceWithTax.amountInMinorUnits;

// `plan.id` on both sides — `tier` is shared by every legacy `Access Pass (n)`.
const isPlanFor = (plan: Membership, held: UserMembership) =>
  plan.id === held.plan.id;

const isTrial = (plan: Membership) => plan.kind === 'trial';

// `cancelling` too: a member sits there until the gateway's webhook lands, which
// can be never.
const isLapsing = (membership: UserMembership) =>
  membership.status === 'cancelling' || membership.status === 'cancelled';

// A zero-limit perk is one the trial doesn't include, not one it's used up. Null
// usage is "unknown", so it never reads as exhausted.
const hasExhaustedPerks = (membership: UserMembership) => {
  const included = Object.values(membership.perksUsage?.perks ?? {}).filter(
    (perk) => perk.isUnlimited || perk.limit > 0
  );
  return (
    included.length > 0 &&
    included.every((perk) => !perk.isUnlimited && perk.remaining === 0)
  );
};

/** For a surface drawing one held membership, with no catalog to hand. */
export const hasUsedUpTrial = (membership: UserMembership) =>
  membership.plan.kind === 'trial' && hasExhaustedPerks(membership);

// `upcoming` rows never started (pending, or called off before the first
// charge), so they don't count as having been a member.
const startedMemberships = (pastMemberships: UserMembership[]) =>
  pastMemberships.filter((m) => m.phase !== 'upcoming');

export type MemberState =
  | { kind: 'signed-out' }
  /** `/me` never reports this alongside `membership`, so they hold nothing else. */
  | { kind: 'confirming'; pendingId: string }
  | { kind: 'new' }
  /**
   * A cancelled trial stays here: a one-shot pass has nothing to renew. `plan`
   * is undefined when theirs was deactivated — the catalog is active-only.
   */
  | {
      kind: 'on-trial';
      membership: UserMembership;
      plan: Membership | undefined;
      perksUsedUp: boolean;
    }
  | { kind: 'trial-ended' }
  /** `plan` is undefined when theirs was deactivated — the catalog is active-only. */
  | { kind: 'member'; membership: UserMembership; plan: Membership | undefined }
  | {
      kind: 'lapsing';
      membership: UserMembership;
      plan: Membership | undefined;
    }
  | { kind: 'ex-member' };

export function deriveMemberState({
  user,
  pastMemberships,
  plans,
}: {
  user: UserProfile | null;
  pastMemberships: UserMembership[];
  plans: Membership[];
}): MemberState {
  if (!user) return { kind: 'signed-out' };
  if (user.pendingMembership)
    return { kind: 'confirming', pendingId: user.pendingMembership.id };

  const current = user.membership;
  if (current) {
    const plan = plans.find((p) => isPlanFor(p, current));
    if (current.plan.kind === 'trial') {
      return {
        kind: 'on-trial',
        membership: current,
        plan,
        perksUsedUp: hasExhaustedPerks(current),
      };
    }
    return isLapsing(current)
      ? { kind: 'lapsing', membership: current, plan }
      : { kind: 'member', membership: current, plan };
  }

  const started = startedMemberships(pastMemberships);
  // `hasUsedUpTrialPass` answers before the history lands.
  if (started.length === 0)
    return user.hasUsedUpTrialPass ? { kind: 'trial-ended' } : { kind: 'new' };
  return started.every((m) => m.plan.kind === 'trial')
    ? { kind: 'trial-ended' }
    : { kind: 'ex-member' };
}

// ── The upsell ───────────────────────────────────────────────────────────

export type MembershipUpsell = {
  /** Picks the section's copy; the lists below are what it draws. */
  state: MemberState;
  /** The plan a lapsing member can buy again — a different pitch from "trade up". */
  renewablePlan: Membership | null;
  /** What's still buyable ABOVE what they hold, cheapest first. */
  membershipsToUpsell: Membership[];
};

/** Exported so a surface can drop its own heading along with the section. */
export const hasPlansOnOffer = (upsell: MembershipUpsell) =>
  upsell.renewablePlan !== null || upsell.membershipsToUpsell.length > 0;

export function deriveMembershipUpsell(input: {
  user: UserProfile | null;
  pastMemberships: UserMembership[];
  plans: Membership[];
}): MembershipUpsell {
  const state = deriveMemberState(input);
  const byPrice = [...input.plans].sort((a, b) => priceOf(a) - priceOf(b));
  const subscriptions = byPrice.filter((plan) => !isTrial(plan));
  // With no price to compare against, offer the rest rather than nothing.
  const above = (held: Membership | undefined) =>
    held
      ? subscriptions.filter(
          (plan) => plan.id !== held.id && priceOf(plan) > priceOf(held)
        )
      : subscriptions;
  const offer = (
    membershipsToUpsell: Membership[],
    renewablePlan: Membership | null = null
  ): MembershipUpsell => ({ state, renewablePlan, membershipsToUpsell });

  switch (state.kind) {
    case 'confirming':
      return offer([]);
    case 'signed-out':
    case 'new':
      return offer(byPrice);
    case 'trial-ended':
    case 'ex-member':
      return offer(subscriptions);
    case 'on-trial':
    case 'member':
      return offer(above(state.plan));
    case 'lapsing':
      return offer(above(state.plan), state.plan ?? null);
  }
}

// ── The members-only band ────────────────────────────────────────────────

/**
 * The one plan card under an event's members-only tiers, or null where the
 * tiers' own notices already say enough.
 */
export function derivePlanToUpsellInMembersOnlyTicketTierGroup(input: {
  user: UserProfile | null;
  pastMemberships: UserMembership[];
  plans: Membership[];
  hasNoTrialTicketTiers: boolean;
}): Membership | null {
  const state = deriveMemberState(input);
  const byPrice = [...input.plans].sort((a, b) => priceOf(a) - priceOf(b));
  const accessPass = byPrice.find((plan) => !isTrial(plan)) ?? null;

  switch (state.kind) {
    case 'new':
      return input.hasNoTrialTicketTiers
        ? accessPass
        : (byPrice.find(isTrial) ?? accessPass);
    case 'trial-ended':
    case 'ex-member':
      return accessPass;
    case 'on-trial':
      return state.perksUsedUp || input.hasNoTrialTicketTiers
        ? accessPass
        : null;
    case 'signed-out':
    case 'confirming':
    case 'member':
    case 'lapsing':
      return null;
  }
}

/**
 * Whether today's charge is a part-month rather than the plan's price.
 *
 * Cycles are calendar-month aligned: a plan bought mid-month is charged pro rata
 * for the days left and then bills in full from the 1st, while one bought ON the
 * 1st is already aligned and the server reports the two figures as equal — which
 * is the test. Compared in minor units; the `display` strings are the server's
 * own words and are never arithmetic here. A one-shot plan sends no prorated
 * figures at all.
 */
export const isProrated = (plan: Membership) =>
  plan.proratedPriceWithTax !== null &&
  plan.proratedPriceWithTax.amountInMinorUnits !==
    plan.priceWithTax.amountInMinorUnits;

/**
 * A restart inside a month they've already paid for (cancelled, then bought
 * again before the 1st): the server prorates today's charge down to zero.
 */
export const isNothingDueToday = (plan: Membership) =>
  isProrated(plan) && plan.proratedPriceWithTax?.amountInMinorUnits === 0;

// ── The purchase gate ────────────────────────────────────────────────────

export type PurchaseGate =
  | { kind: 'signed-out' }
  | { kind: 'buy' }
  /** An unconfirmed purchase they must finish or call off first, on its own page. */
  | { kind: 'confirming'; pendingId: string }
  /** Takes effect at once. */
  | { kind: 'upgrade'; from: UserMembership }
  /** `startsOn` is when the cheaper plan takes over: the end of the paid cycle. */
  | { kind: 'downgrade'; from: UserMembership; startsOn: string | null }
  /** Re-buying the very plan that's lapsing. */
  | { kind: 'renew'; from: UserMembership }
  | { kind: 'already-member'; from: UserMembership }
  /** The trial is for first-time members only. */
  | { kind: 'trial-ineligible' };

/** Whether the gate lets this press take money — `false` on every no-sale state. */
export const canPurchase = (gate: PurchaseGate) =>
  gate.kind === 'buy' ||
  gate.kind === 'upgrade' ||
  gate.kind === 'downgrade' ||
  gate.kind === 'renew';

export function derivePurchaseGate({
  plans,
  user,
  membershipBeingViewed: viewed,
  pastMemberships,
}: {
  plans: Membership[];
  user: UserProfile | null;
  membershipBeingViewed: Membership;
  pastMemberships: UserMembership[];
}): PurchaseGate {
  const state = deriveMemberState({ user, pastMemberships, plans });

  switch (state.kind) {
    case 'signed-out':
      return { kind: 'signed-out' };
    case 'confirming':
      return { kind: 'confirming', pendingId: state.pendingId };
    case 'new':
      return { kind: 'buy' };
    case 'trial-ended':
    case 'ex-member':
      return isTrial(viewed) ? { kind: 'trial-ineligible' } : { kind: 'buy' };
  }

  // Holding one: on-trial, member, lapsing.
  const from = state.membership;
  if (isPlanFor(viewed, from)) {
    return state.kind === 'lapsing'
      ? { kind: 'renew', from }
      : { kind: 'already-member', from };
  }
  if (isTrial(viewed)) return { kind: 'trial-ineligible' };
  // No price to compare: a purchase we can't justify blocking gets allowed.
  if (!state.plan || priceOf(viewed) > priceOf(state.plan)) {
    return { kind: 'upgrade', from };
  }
  // A renewing membership ends at `renewsAt`, a lapsing one at `expiresAt`.
  return {
    kind: 'downgrade',
    from,
    startsOn: from.renewsAt ?? from.expiresAt,
  };
}
